import { AxiosError } from 'axios'

import { DocumentType } from '@lyrahealth-inc/shared-app-logic/src/models/document/Document'

import { axiosAuthInstance } from '../../../common/http/axiosInstance'
import { baseApi, RTKQueryTag } from '../../../data/baseApi'

export type Template = {
  document_template_id: string
  file_name: string
  document_type: DocumentType
  create_date: string
  update_date: string
  autosend_program_ids?: string[]
  signing_flow?: string
}

type GetTemplatesResponse = {
  document_templates: Template[]
}

type UploadTemplateArgs = {
  file: File
  fileName: string
  documentType: string
  autoSend: string[]
  signingFlow?: string
  providerId?: string
}

type DownloadTemplateArgs = {
  templateId: string
  providerId?: string
}

export type GetTemplateEditorUrlArgs = {
  templateId: string
  returnUrl: string
  providerId?: string
}

export type GetTemplateEditorUrlResponse = {
  editor_url: string
}

export type UpdateTemplateArgs = {
  templateId: string
  fileName: string
  documentType: string
  autoSend: string[]
  providerId?: string
}

type DeleteTemplateArgs = {
  templateId: string
  providerId?: string
}

const templatesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDocumentTemplates: builder.query<GetTemplatesResponse, { providerId?: string } | void>({
      query: (args) => ({
        method: 'get',
        url: '/lt/v1/documents/templates',
        ...(args?.providerId && { params: { provider_id: args.providerId } }),
      }),
      providesTags: [RTKQueryTag.DOCUMENT_TEMPLATE],
    }),
    uploadDocumentTemplate: builder.mutation<Template, UploadTemplateArgs>({
      queryFn: async ({ file, fileName, documentType, autoSend, signingFlow, providerId }) => {
        try {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('file_name', fileName)
          formData.append('document_type', documentType)
          autoSend.forEach((program) => formData.append('autosend_program_ids', program))
          if (signingFlow) {
            formData.append('signature_required', 'true')
            formData.append('signing_flow', signingFlow)
          }
          const url = providerId
            ? `/lt/v1/documents/templates?provider_id=${encodeURIComponent(providerId)}`
            : '/lt/v1/documents/templates'
          const response = await axiosAuthInstance.post<Template>(url, formData)
          return { data: response.data }
        } catch (error) {
          const axiosError = error as AxiosError
          return {
            error: {
              status: axiosError.response?.status,
              data: axiosError.response?.data,
              message: axiosError.message,
            },
          }
        }
      },
      invalidatesTags: [RTKQueryTag.DOCUMENT_TEMPLATE],
    }),
    downloadDocumentTemplate: builder.mutation<Blob, DownloadTemplateArgs>({
      queryFn: async ({ templateId, providerId }) => {
        try {
          const response = await axiosAuthInstance.get<Blob>(`/lt/v1/documents/templates/${templateId}`, {
            responseType: 'blob',
            ...(providerId && { params: { provider_id: providerId } }),
          })
          return { data: response.data }
        } catch (error) {
          const axiosError = error as AxiosError
          return {
            error: {
              status: axiosError.response?.status,
              data: axiosError.response?.data,
              message: axiosError.message,
            },
          }
        }
      },
    }),
    updateDocumentTemplate: builder.mutation<void, UpdateTemplateArgs>({
      query: ({ templateId, fileName, documentType, autoSend, providerId }) => ({
        method: 'patch',
        url: `/lt/v1/documents/templates/${templateId}`,
        data: { file_name: fileName, document_type: documentType, autosend_program_ids: autoSend },
        ...(providerId && { params: { provider_id: providerId } }),
      }),
      invalidatesTags: [RTKQueryTag.DOCUMENT_TEMPLATE],
    }),
    deleteDocumentTemplate: builder.mutation<void, DeleteTemplateArgs>({
      query: ({ templateId, providerId }) => ({
        method: 'delete',
        url: `/lt/v1/documents/templates/${templateId}`,
        ...(providerId && { params: { provider_id: providerId } }),
      }),
      invalidatesTags: [RTKQueryTag.DOCUMENT_TEMPLATE],
    }),
    getTemplateEditorUrl: builder.query<GetTemplateEditorUrlResponse, GetTemplateEditorUrlArgs>({
      query: ({ templateId, returnUrl, providerId }) => ({
        method: 'get',
        url: `/lt/v1/documents/templates/${templateId}`,
        params: { return_url: returnUrl, ...(providerId && { provider_id: providerId }) },
      }),
    }),
  }),
})

export const {
  useGetDocumentTemplatesQuery,
  useDownloadDocumentTemplateMutation,
  useUploadDocumentTemplateMutation,
  useUpdateDocumentTemplateMutation,
  useDeleteDocumentTemplateMutation,
  useLazyGetTemplateEditorUrlQuery,
} = templatesApi

export default templatesApi
